// TripBadgePDF.js

import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import logo from "./logoo.png";

// ==================================================
// CONFIGURATION
// ==================================================
const ITEMS_PER_PAGE = 2;

// ==================================================
// HELPERS
// ==================================================
const chunkArray = (items, size) => {
  const pages = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages;
};

// ==================================================
// STYLES
// ==================================================
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#F4F4F4",
    paddingTop: 25,
    paddingBottom: 25,
    paddingHorizontal: 30,
  },

  pageContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  card: {
    width: "48%",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    border: "1.5 solid #000000",
    overflow: "hidden",
    minHeight: 450,
  },

  header: {
    backgroundColor: "#e0d9d9",
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: "row",
    alignItems: "center",
  },

  logo: {
    width: 46,
    height: 46,
    marginRight: 12,
  },

  companyInfo: {
    flex: 1,
  },

  companyName: {
    color: "#000000",
    fontSize: 15,
    fontWeight: "bold",
    letterSpacing: 1,
  },

  tripText: {
    color: "#000000",
    fontSize: 9,
    marginTop: 3,
  },

  body: {
    padding: 18,
  },

  section: {
    marginBottom: 14,
  },

  label: {
    fontSize: 8,
    color: "#777777",
    textTransform: "uppercase",
    marginBottom: 4,
    letterSpacing: 1,
  },

  value: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#111111",
  },

  addressContainer: {
    backgroundColor: "#F8F8F8",
    border: "1 solid #E5E5E5",
    borderRadius: 10,
    padding: 10,
  },

  addressText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#222222",
  },

  trackingBox: {
    marginTop: 20,
    backgroundColor: "#b3afaf",
    borderRadius: 12,
    paddingVertical: 18,
    alignItems: "center",
  },

  trackingLabel: {
    color: "#000000",
    fontSize: 8,
    letterSpacing: 2,
    marginBottom: 6,
  },

  trackingCode: {
    color: "#000000",
    fontSize: 22,
    fontWeight: "bold",
    letterSpacing: 4,
  },

  footer: {
    marginTop: 18,
    borderTop: "1 solid #EAEAEA",
    paddingTop: 10,
    alignItems: "center",
  },

  footerText: {
    fontSize: 8,
    color: "#666666",
  },

  pageNumber: {
    position: "absolute",
    bottom: 10,
    right: 20,
    fontSize: 8,
    color: "#666666",
  },
});

// ==================================================
// COMPONENT
// ==================================================
const TripBadgePDF = ({ shipments = [], tripName = "" }) => {
  const pages = chunkArray(shipments, ITEMS_PER_PAGE);

  // ❗ IMPORTANT: prevent rendering empty document
  if (!shipments || shipments.length === 0) {
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Text>No shipments available</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document
      title={`Trip ${tripName}`}
      author="Zulu Whiskey Shipping"
      subject="Shipment Manifest"
      creator="Zulu Whiskey Shipping System"
      producer="React PDF"
    >
      {pages.map((pageShipments, pageIndex) => (
        <Page
          key={`page-${pageIndex}`}
          size="A4"
          orientation="landscape"
          style={styles.page}
          wrap={false}
        >
          <View style={styles.pageContainer}>
            {pageShipments.map((shipment, index) => (
              <View
                key={
                  shipment.id ||
                  shipment.tracking_code ||
                  `ship-${index}`
                }
                style={styles.card}
              >
                {/* HEADER */}
                <View style={styles.header}>
                  <Image src={logo} style={styles.logo} />

                  <View style={styles.companyInfo}>
                    <Text style={styles.companyName}>
                      ZULU WHISKEY SHIPPING
                    </Text>
                    <Text style={styles.tripText}>
                      TRIP: {tripName}
                    </Text>
                  </View>
                </View>

                {/* BODY */}
                <View style={styles.body}>
                  <View style={styles.section}>
                    <Text style={styles.label}>
                      Customer Name
                    </Text>
                    <Text style={styles.value}>
                      {shipment.customer_name || "N/A"}
                    </Text>
                  </View>

                  <View style={styles.section}>
                    <Text style={styles.label}>
                      Receiver Name
                    </Text>
                    <Text style={styles.value}>
                      {shipment.receiver_name || "N/A"}
                    </Text>
                  </View>

                  <View style={styles.section}>
                    <Text style={styles.label}>
                      Receiver Address
                    </Text>
                    <View style={styles.addressContainer}>
                      <Text style={styles.addressText}>
                        {shipment.receiver_address ||
                          "No address provided"}
                      </Text>
                    </View>
                  </View>

                  {/* TRACKING */}
                  <View style={styles.trackingBox}>
                    <Text style={styles.trackingLabel}>
                      TRACKING CODE
                    </Text>
                    <Text style={styles.trackingCode}>
                      {shipment.tracking_code || "N/A"}
                    </Text>
                  </View>

                  {/* FOOTER */}
                  <View style={styles.footer}>
                    <Text style={styles.footerText}>
                      www.zuluwhiskeyshipping.com
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>

          {/* PAGE NUMBER */}
          <Text style={styles.pageNumber}>
            Page {pageIndex + 1} / {pages.length}
          </Text>
        </Page>
      ))}
    </Document>
  );
};

export default TripBadgePDF;